import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import { AddTaskModal } from "components/crm/AddTaskModal";
import DashboardContainer from "components/dashboard/DashboardContainer";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import { Header } from "components/DesignSystem/Header/Header";
import { Search } from "components/DesignSystem/Search/Search";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import { Diamond } from "components/icons/Diamond";
import { SmallPlusIcon } from "components/icons/export const SmallPlusIcon";
import Wind from "components/icons/Wind";
import { PermissionBasedView } from "components/PermissionBasedView/PermissionBasedView";
import { CLICKED_SEARCH_ON_FILINGS } from "constants/analyticsEvents";
import { FILINGS_PAGE } from "constants/subscriptionPermissionFeatures";
import {
  COMPLETED_FILTER_KEYS,
  FILTER_FILING_ALL,
  FILTER_FILING_COMPLETED,
  FILTER_FILING_ONGOING,
  FILTER_FILING_PROCESSING,
  ONGOING_KEYS,
  PROCESSING_KEYS,
  TaskFilterKeys,
  TaskFilterValues,
} from "constants/task";
import { AnimatePresence } from "framer-motion";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { TaxSubscriptionModal } from "pages/Tax/TaxSubscriptionModal";
import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useRouteMatch } from "react-router-dom";
import {
  useGetAllTaskSeasonsQuery,
  useGetTaskSeasonsQuery,
  useGetTasksStatsQuery,
} from "store/apis/filing";
import { taskAlarmApis } from "store/apis/taskList";
import { setTaskSliderHeight } from "store/slices/taskList";
import { debounce } from "utils/debouncing";
import { CompanyTaskOverview } from "./CompanyTaskOverview";
import { EntityTaskList } from "./EntityTaskList/EntityTaskList";
import { SelectFinancialYear } from "./SelectFinancialYear";
import { TaskListFilter } from "./TaskListFilter";
import { TaskSkeletonComponent } from "./EntityTaskList/EntityTaskTable";
import { useCurrentGroup } from "hooks/useCurrentGroup";

export type TaskListFilterOptions = TaskFilterValues;

export type TaskListFilterType = {
  updateFilter: <S extends TaskFilterKeys>(
    name: S,
    newValue: TaskFilterValues[S]
  ) => void;
  values: TaskFilterValues;
};

const initialValue: TaskListFilterOptions = {
  FILING_STATUS: FILTER_FILING_ALL,
  START_DATE: "",
  END_DATE: "",
  SELECT_PERIOD: "",
  FILINGS: [],
  PENDING_ON_ME: false,
};

export const refreshTaskListFor30Seconds = (
  invalidateApi: () => void
): Promise<void> => {
  return new Promise((resolve) => {
    let count = 0;
    const interval = setInterval(() => {
      count += 2;
      if (count >= 30) {
        clearInterval(interval);
        resolve();
      }
      invalidateApi();
    }, 2000);
  });
};

export const TaskList = () => {
  usePageTitle("Filings");
  const { url } = useRouteMatch();
  const { update } = useUpdateQuery();
  const query = useQuery();
  const { trackEvent } = useAnalytics();
  const checkForTasksLaunch = query.get("check_for_tasks");
  const search = query.get("search");
  const dispatch = useDispatch();
  const rRef = useRef(null);
  const selectedSeasons = query.get("seasons");
  const { values, updateFilter } = useFilters<TaskListFilterOptions>({
    initialValue: initialValue,
    useQueryParams: true,
  });
  const { isAdmin, isCustomer } = useRoleBasedView();
  const subscriptionModal = useModal();
  const entityId = useCurrentEntityId();
  const { secondary_channel_url: generalHelpChannel } =
    useCurrentGroupContext();
  const modalDescription =
    "Unlock access to all tax & compliance filings and more with Tax Standard Plan";

  const { uuid: groupId } = useCurrentGroupContext();

  let selectedStatus: string | null = values.FILING_STATUS;

  if (selectedStatus === FILTER_FILING_ALL) {
    selectedStatus = null;
  } else if (selectedStatus === FILTER_FILING_COMPLETED) {
    selectedStatus = COMPLETED_FILTER_KEYS;
  } else if (selectedStatus === FILTER_FILING_PROCESSING) {
    selectedStatus = PROCESSING_KEYS;
  } else if (selectedStatus === FILTER_FILING_ONGOING) {
    selectedStatus = ONGOING_KEYS;
  }

  const baseQueryParams = {
    groupId: groupId || "",
    status_filter: selectedStatus || null,
    base_task_templates: values.FILINGS.join(",") || null,
    deadline_start: values.START_DATE || null,
    deadline_end: values.END_DATE || null,
    action_pending_on_user: values.PENDING_ON_ME || null,
    search_term: search || null,
  };

  const { data: seasonList } = useGetTaskSeasonsQuery(
    {
      groupId,
    },
    { skip: !groupId }
  );

  const {
    data: taskSeasons,
    isSuccess: taskSeasonsSuccess,
    isLoading: taskSeasonsLoading,
    isFetching: taskSeasonsFetching,
    isUninitialized: taskSeasonsUninitialized,
  } = useGetAllTaskSeasonsQuery(
    { ...baseQueryParams, filing_seasons: selectedSeasons },
    { skip: !groupId }
  );

  const seasons = selectedSeasons
    ? selectedSeasons.replace("all,", "")
    : seasonList?.seasons?.join(",") || null;

  const taskQueryParams = {
    ...baseQueryParams,
    filing_seasons: seasons,
  };

  const {
    data: taskStats,
    isLoading,
    isUninitialized,
  } = useGetTasksStatsQuery(taskQueryParams, {
    skip: !groupId,
  });

  const handleChange = debounce((e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    update({ query: "search", value: value || null });
  });

  const handleSearchClick = () => {
    trackEvent(CLICKED_SEARCH_ON_FILINGS);
  };

  const {
    successToast,
    loading: loadingToast,
    dismiss: dismissToast,
  } = useToast();

  useEffect(() => {
    //@ts-ignore
    const yCoordinate = rRef.current.getBoundingClientRect();

    dispatch(setTaskSliderHeight(window.innerHeight - yCoordinate.y - 89));
  }, [window.innerHeight]);

  useEffect(() => {
    const startCheckingForTasks = async () => {
      const loadingtoastId = loadingToast({
        title: "Launching filings",
        message: "Your filings are being created, please wait.",
      });
      try {
        await refreshTaskListFor30Seconds(() =>
          dispatch(taskAlarmApis.util.invalidateTags(["TaskList"]))
        );

        update({
          query: "check_for_tasks",
          value: null,
        });

        successToast({
          title: "Filings from the cart have been launched.",
        });
      } catch {
      } finally {
        dismissToast(loadingtoastId);
      }
    };

    if (checkForTasksLaunch) {
      startCheckingForTasks();
    }
  }, [checkForTasksLaunch]);

  const resetFilter = () => {
    update({ query: "search", value: null });
    Object.keys(initialValue).forEach((key) => {
      updateFilter(
        key as keyof TaskListFilterOptions,
        initialValue[key as keyof TaskListFilterOptions]
      );
    });
  };

  const isAnyFilterActive = Object.values(values).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );

  const seasonOptions = seasonList
    ? [
        { label: "All Financial years", value: "all" },
        ...seasonList.seasons.map((season) => ({
          label: season,
          value: season,
        })),
      ]
    : [
        {
          label: selectedSeasons || "All Financial years",
          value: selectedSeasons || "all",
        },
      ];

  let isEmpty = taskSeasons?.entities.every((entity) =>
    Object.values(entity.season_task_counts).every((count) => count === 0)
  );

  const isSeasonLoading = taskSeasonsLoading || taskSeasonsUninitialized;
  const isTaskCountLoading = isLoading || isUninitialized;

  return (
    <>
      <DashboardContainer
        ref={rRef}
        className="t-h-full t-border-none t-px-8 sm:t-px-2"
      >
        <DashboardContainer.Header className="t-z-header">
          <Header
            v2
            title="Filings"
            right={
              isAdmin ? (
                <PermissionBasedView
                  route="/tax/filings"
                  feature={FILINGS_PAGE}
                  isNav
                  blockedUI={
                    <Button
                      onClick={() => subscriptionModal.open()}
                      size="small"
                      customType="primary"
                    >
                      <div className="t-flex t-items-center t-gap-1.5">
                        <Diamond />
                        Add Filing
                      </div>
                    </Button>
                  }
                >
                  <ConditionalLink to={`${url}/addtask`}>
                    <Button size="small" customType="primary">
                      <SmallPlusIcon className="t-text-surface t-mr-1" />
                      Add Filing
                    </Button>
                  </ConditionalLink>
                </PermissionBasedView>
              ) : isCustomer ? (
                <ConditionalLink to={`/settings/services?entity=${entityId}`}>
                  <Button size="regular" customType="ghost">
                    <SmallPlusIcon className="t-mr-1" />
                    Add filing
                  </Button>
                </ConditionalLink>
              ) : null
            }
            bottom={
              <>
                <div className="t-flex t-justify-between t-gap-2 t-mb-3 sm:t-flex-wrap">
                  <div className="t-flex t-flex-wrap t-gap-2 t-items-center t-min-w-0 sm:t-contents">
                    <div className="t-w-48 t-shrink-0 sm:t-order-1 sm:t-w-auto sm:t-min-w-0 sm:t-flex-1">
                      <SelectFinancialYear seasons={seasonOptions} />
                    </div>
                    <div className="t-contents sm:t-order-3 sm:t-flex sm:t-w-full sm:t-flex-wrap sm:t-items-center sm:t-gap-2">
                      <Search
                        className={classNames("t-transition-all t-shrink-0", {
                          "t-w-60 sm:t-w-auto sm:t-min-w-0 sm:t-flex-1": search,
                          "t-w-8 focus:t-w-60 sm:focus:t-w-auto sm:focus:t-min-w-0 sm:focus:t-flex-1":
                            !search,
                        })}
                        onChange={handleChange}
                        onClick={handleSearchClick}
                        defaultValue={search || ""}
                        hidePlaceholder
                      />
                      <AnimatePresence>
                        <TaskListFilter
                          values={values}
                          updateFilter={updateFilter}
                        />
                      </AnimatePresence>
                    </div>
                  </div>
                </div>

                <CompanyTaskOverview
                  taskCounts={taskStats?.status_counts}
                  isFetching={
                    (isSeasonLoading || isTaskCountLoading) &&
                    !checkForTasksLaunch
                  }
                  allSeasons={seasonList?.seasons?.join(",") || ""}
                  values={values}
                  updateFilter={updateFilter}
                />
              </>
            }
            className="t-border-none t-space-y-4"
          />
        </DashboardContainer.Header>
        <DashboardContainer.Content className="t-my-4">
          <Async.Root
            {...{
              isLoading: isSeasonLoading,
              isEmpty: Boolean(isEmpty),
              isSuccess: taskSeasonsSuccess,
            }}
            renderOnLoad={
              <div className="t-space-y-8 t-mt-6 t-h-full t-w-full">
                <TaskSkeletonComponent />
                <TaskSkeletonComponent />
                <TaskSkeletonComponent />
                <TaskSkeletonComponent />
                <TaskSkeletonComponent />
                <TaskSkeletonComponent />
              </div>
            }
          >
            <Async.Empty>
              <div className="t-flex t-flex-col t-bg-surface-lighter-grey t-rounded-md t-justify-center t-items-center t-w-full t-py-28">
                <Wind className="t-text-neutral-40" />
                <div className="t-text-subtext t-text-text-60 t-mt-4">
                  {isAnyFilterActive
                    ? "No filings found"
                    : "No filings to show"}
                </div>
                <div className="t-text-body-sm t-text-text-30 t-mb-4 t-px-4 t-text-center">
                  {isAnyFilterActive
                    ? "Try adjusting or clearing your filters to see more results"
                    : "We couldn’t fetch any required or active filings. If you think something’s missing, our team can help."}
                </div>
                {!isAnyFilterActive ? (
                  <Button size="xsmall" onClick={resetFilter}>
                    Reset to default view
                  </Button>
                ) : (
                  <ConditionalLink to={`chat/${generalHelpChannel ?? ""}`}>
                    <Button size="small">Chat with the team</Button>
                  </ConditionalLink>
                )}
              </div>
            </Async.Empty>
            <Async.Success>
              {taskSeasons && (
                <EntityTaskList
                  taskSeasonsWithEntities={taskSeasons.entities}
                  isParentFetching={taskSeasonsFetching && !checkForTasksLaunch}
                  isAnyFilterActive={isAnyFilterActive}
                  resetFilter={resetFilter}
                  params={baseQueryParams}
                  isFetching={taskSeasonsFetching}
                />
              )}
            </Async.Success>
          </Async.Root>
        </DashboardContainer.Content>
      </DashboardContainer>
      <TaxSubscriptionModal
        show={subscriptionModal.isOpen}
        onClose={() => subscriptionModal.close()}
        entityId={entityId || ""}
        groupId={groupId}
        description={
          <div className="t-pt-1 t-pb-4 t-text-subtext t-text-text-60">
            {modalDescription}
          </div>
        }
      />

      <AddTaskModal />
    </>
  );
};
